import React from "react";
import { useEffect, useState } from "react";

import { Common } from "../../CommonService";
import { Box, Text } from "@chakra-ui/react";
import { dashboardTabs } from "../../modals/Enums/DashboardTabs";
import { ReactSVG } from "react-svg";
import { CommonMethodService } from "../../Services/CommonMethodService";
import { IBodyshopService } from "../../UserService";
import { AdminChecklistItems, WorkTypeValues } from "../../CommonProps";

interface IDashboardButton {
  name: string;
  key: string;
  image: string;
  isActionRequired: boolean;
  isCompleted: boolean;
  hide?: boolean;
}

const icons = {
  estimate: "svg/estimate.svg",
  booking: "svg/calendar.svg",
  repairTracker: "svg/repair.svg",
  feedback: "svg/feedback.svg",
  completed: "svg/completed.svg",
  home: "svg/home.svg",
  bookingInspection: "svg/bookingInspection.svg",
};

const TabButtons = ({
  estimateDetails,
  report,
  currProgress,
  dashboardSelectedTab,
  isEstimateVisible,
  handleDashboardSelectedTab,
}: any) => {
  const [dashboardButtons, setDashboardButtons] = useState<IDashboardButton[]>(
    []
  );
  const ibsService = new IBodyshopService();
  const commonMethodService = new CommonMethodService();
  const common = new Common();

  const estimateAction = common.isEstimateScreenActionRequired(
    estimateDetails,
    ibsService.isLinkOpenedFromEmail
  );
  const isBookingActionRequired = common.isBookingScreenActionRequired(
    estimateDetails,
    ibsService.isLinkOpenedFromEmail
  );
  const isRepairTrackerActionRequired = !isBookingActionRequired
    ? common.isRepairTrackerActionRequired(
        estimateDetails,
        currProgress?.status,
        ibsService.isLinkOpenedFromEmail
      )
    : null;
  const isEstimateFeedbackRequired = estimateDetails?.isVehicleCollected;

  const isEstimateActionRequired = !!estimateDetails?.trackables?.find(
    (x: any) => x.id === "9ES"
  )
    ? true
    : estimateAction;

  const isEstimateActionCompleted = !estimateAction
    ? estimateDetails?.canCancelAuthority
    : !estimateAction;

  useEffect(() => {
    // const hideInspectionButton =
    //   estimateDetails?.expectedArrival ||
    //   estimateDetails?.expectedCompletion ||
    //   estimateDetails?.expectedEnd ||
    //   estimateDetails?.expectedPickup ||
    //   estimateDetails?.expectedStart ||
    //   estimateDetails?.isCancelled ||
    // estimateDetails.jobIssued

    const requiredChecklistItems = [
      AdminChecklistItems.ONBOARDING_FLOW,
      AdminChecklistItems.BOOKING_FLOW,
    ];

    const hasAnyFlow = estimateDetails.adminCheckList.some(
      (checklistItem: string) =>
        requiredChecklistItems.some((requiredItem) =>
          checklistItem.includes(requiredItem)
        )
    );

    const isOnBoardingFlow = estimateDetails.adminCheckList.some(
      (checklistItem: string) =>
        checklistItem.includes(AdminChecklistItems.ONBOARDING_FLOW)
    );
    const isBookingFlow = estimateDetails.adminCheckList.some(
      (checklistItem: string) =>
        checklistItem.includes(AdminChecklistItems.BOOKING_FLOW)
    );

    const isWorkTypeExist = WorkTypeValues.find(
      (workType: any) => workType === estimateDetails.workType
    );

    const buttons = [
      {
        name: "Home",
        key: dashboardTabs.home,
        image: icons.home,
        isActionRequired: true,
        isCompleted: false,
        hide: false,
      },
      {
        name: "My\nEstimate",
        key: dashboardTabs.estimateDetails,
        image: icons.estimate,
        isActionRequired: isEstimateActionRequired,
        isCompleted: isEstimateActionCompleted,
        hide: !isEstimateVisible,
      },
      {
        name: "Booking\nDetails",
        key: dashboardTabs.bookingDetail,
        image: icons.booking,
        isActionRequired: isBookingFlow && !estimateDetails?.isCancelled,
        isCompleted: !estimateDetails?.cancelled && !!isWorkTypeExist,
        hide: !isBookingFlow || estimateDetails?.isCancelled,
      },
      {
        name: "Book\nInspection",
        key: dashboardTabs.bookingInspection,
        image: icons.bookingInspection,
        isActionRequired: !estimateDetails?.isCancelled,
        isCompleted:
          !!estimateDetails?.jobNumber && !estimateDetails?.cancelled,
        hide:
          (hasAnyFlow && isBookingFlow) ||
          !isOnBoardingFlow ||
          estimateDetails?.isCancelled,
      },
      {
        name: "Repair\nTracker",
        key: dashboardTabs.repairTracker,
        image: icons.repairTracker,
        isActionRequired: isRepairTrackerActionRequired,
        isCompleted: isRepairTrackerActionRequired === false,
        hide: false,
      },
      {
        name: "Feedback",
        key: dashboardTabs.estimateFeedback,
        image: icons.feedback,
        isActionRequired: !!isEstimateFeedbackRequired,
        isCompleted: !!isEstimateFeedbackRequired,
        hide: false,
      },
      {
        name: "Rewards\nProgram",
        key: dashboardTabs.flowGetter,
        image: icons.completed,
        isActionRequired: !estimateAction,
        isCompleted: !estimateAction,
        hide: !estimateDetails?.trackables?.find(
          (trackable: any) => trackable.id === "9RE"
        ),
      },
      {
        name: "Report",
        key: dashboardTabs.report,
        image: icons.estimate,
        isActionRequired: report?.is_acknowledged,
        isCompleted: report?.is_acknowledged,
        hide: !report,
      },
    ];
    setDashboardButtons(buttons);
  }, [
    estimateDetails,
    isEstimateVisible,
    isEstimateActionRequired,
    isEstimateActionCompleted,
    isRepairTrackerActionRequired,
    isEstimateFeedbackRequired,
    estimateAction,
    report,
  ]);

  const handleClick = (
    key: string,
    isCompleted: boolean,
    isActionRequired: boolean
  ) => {
    if (isCompleted || isActionRequired) {
      const estimateId = estimateDetails?.estimateNumber || 0;
      commonMethodService.setQueryParams(estimateId, key);
      handleDashboardSelectedTab(key);
    }
  };

  if (!estimateDetails) return null;
  return (
    <Box p={2} pb={6} className="topButtonContainer">
      {dashboardButtons.length &&
        dashboardButtons
          .filter(
            (e) =>
              !e.hide &&
              (e.name !== "Feedback" || estimateDetails?.isVehicleCollected)
          )
          .map((e) => {
            const isSelected = dashboardSelectedTab === e.key;
            const tileClass = `topButtonTile ${
              e.isActionRequired
                ? "active"
                : !e.isCompleted
                ? "disabledTile"
                : ""
            } ${isSelected ? "selectedTab" : ""}`;
            return (
              <Box
                key={e.key}
                rounded="lg"
                boxShadow={isSelected ? "lg" : "md"}
                bg={isSelected ? "gray.100" : "white"}
                border="1px"
                color={isSelected ? "blue.700" : "gray.500"}
                borderColor={isSelected ? "blue.700" : "gray.100"}
                className={tileClass}
                onClick={() =>
                  handleClick(e.key, e.isCompleted, e.isActionRequired)
                }
              >
                <div
                  className={
                    e.isActionRequired
                      ? "actionRequired"
                      : e.isCompleted
                      ? "completed"
                      : "disabledButton"
                  }
                ></div>
                <div className="clearfix"></div>
                <ReactSVG
                  src={e.image}
                  renumerateIRIElements={false}
                  useRequestCache={false}
                  wrapper="div"
                />
                <Text textShadow="2px 2px #00000012">{e.name}</Text>
              </Box>
            );
          })}
    </Box>
  );
};

export default TabButtons;
